import type { GeneratedFile, GeneratorOptions, Schema } from '@cli-types/core-engine/schema.js'
import type { ColumnsGeneratorResult } from '@cli-types/next-adapter/generators.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { pluralize, singularize, toPascalCase } from '@cli-utils/text.js'
import { resolveDisplayNouns } from '@core-engine/schema/display-nouns.js'
import { flattenFields } from '@core-engine/schema/field-helpers/flattening/flatten-fields.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths/resolve-schema-action-import-path.js'
import { buildDeleteDialogSource } from '@next-adapter/generators/row-actions-emitters/build-delete-dialog-source.js'
import { buildRowActionsSource } from '@next-adapter/generators/row-actions-emitters/build-row-actions-source.js'
import { renderColumnsRenderer } from '@next-adapter/templates/renderers/generators/columns/generate-columns.js'
import { generateColumnDef } from './column-defs/generate-column-def.js'
import { generateFirstColumnDef } from './column-defs/generate-first-column-def.js'
import { isSortableColumn } from './column-defs/is-sortable-column.js'
import { createCustomCellComponent } from './custom-cell.js'

export function generateColumns(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): ColumnsGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const columns = schema.columns ?? []
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const displayNouns = resolveDisplayNouns(schema)
  const Actions = `${Singular}Actions`
  const DeleteDialog = `Delete${Singular}Dialog`
  const deleteDialogFileName = `delete-${singular}-dialog`

  const flatFields = flattenFields(schema.fields || [])

  let needsSortIcons = false
  let needsBadge = false
  let needsButton = false
  let needsImageRenderer = false
  let needsTruncate = false

  for (const column of columns) {
    if (isSortableColumn(column)) {
      needsSortIcons = true
      needsButton = true
    }
    if (column.type === 'badge' || column.type === 'boolean') needsBadge = true
    if (column.type === 'image' || column.type === 'avatar') needsImageRenderer = true
    if (column.type === 'link') needsTruncate = true
  }

  const hasDelete = schema.actions?.delete ?? false

  const lucideIcons: string[] = []

  const uiImports: string[] = []
  if (needsButton) uiImports.push("import { Button } from '@admin/components/ui/button'")
  if (needsBadge) uiImports.push("import { Badge } from '@admin/components/ui/badge'")
  if (hasDelete) uiImports.push("import { Checkbox } from '@admin/components/ui/checkbox'")
  if (needsImageRenderer) {
    uiImports.push("import { Avatar, AvatarFallback } from '@admin/components/ui/avatar'")
  }

  const customImports: string[] = []
  const customFiles: GeneratedFile[] = []
  for (const column of columns) {
    if (column.type === 'custom' && column.component) {
      customImports.push(`import { ${column.component} } from './cells/${column.component}'`)
      customFiles.push(...createCustomCellComponent(schema, column.component, pagesDir, options))
    }
  }

  const utilityImports: string[] = []
  if (needsSortIcons) {
    utilityImports.push("import { SortIndicator } from '@admin/components/shared/sort-indicator'")
  }
  if (needsTruncate) utilityImports.push("import { truncateText } from '@admin/utils/text/text'")

  const firstColumn = columns[0]
  const restColumns = columns.slice(1)

  const firstColDef = generateFirstColumnDef(firstColumn, flatFields, { selectable: hasDelete })
  const restColDefs = restColumns.map((col) => generateColumnDef(col, flatFields)).join(',\n')

  const actionsColumn = hasDelete
    ? renderColumnsRenderer({ kind: 'renderColumnsLayoutTemplate', props: { actions: Actions } })
    : ''

  const content = renderColumnsRenderer({
    kind: 'renderColumnsClientModuleTemplate',
    props: {
      lucideIconsLucide:
        lucideIcons.length > 0 ? `import { ${lucideIcons.join(', ')} } from 'lucide-react'` : '',
      needsImageRendererImage: needsImageRenderer ? "import Image from 'next/image'" : '',
      join: uiImports.join('\n'),
      joinText: utilityImports.join('\n'),
      singular: Singular,
      actionImportPath: actionImportPath,
      joinContent: customImports.join('\n'),
      hasDeleteActions: hasDelete ? `import { ${Actions} } from './actions'\n` : '',
      firstColDef: firstColDef,
      restColDefsContent: restColDefs ? `,\n${restColDefs}` : '',
      actionsColumnContent: actionsColumn ? `,\n${actionsColumn}` : ''
    }
  })
  const files = [createGeneratedFile(`${pagesDir}/${schema.name}/columns.tsx`, content)]

  if (hasDelete) {
    files.push(
      createGeneratedFile(
        `${pagesDir}/${schema.name}/actions.tsx`,
        buildRowActionsSource({
          componentName: Actions,
          dialogComponentName: DeleteDialog,
          dialogModuleName: deleteDialogFileName,
          noun: displayNouns.singular
        })
      ),
      createGeneratedFile(
        `${pagesDir}/${schema.name}/${deleteDialogFileName}.tsx`,
        buildDeleteDialogSource({
          componentName: DeleteDialog,
          deleteActionName: `delete${Singular}`,
          actionImportPath,
          noun: displayNouns.singular,
          queryKey: plural
        })
      )
    )
  }

  return {
    files: [...files, ...customFiles]
  }
}
